import { useState, type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import useReveal from "../../hooks/useReveal";
import {
  hiringService,
  type HiringFormData,
} from "../../services/hiringService";
import { toast } from "react-toastify";
import { Input } from "../../components/common/Input/Input";
import { Button } from "../../components/common/Button/Button";
import { InfoCard } from "../../components/InfoCard/InfoCard";
import { SuccessView } from "../../components/SuccessView/SuccessView";
import "./Hiring.css";

function Hiring() {
  const { t } = useTranslation();
  const [form, setForm] = useState<HiringFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    linkedin: "",
    positionName: "",
    cvBase64: "",
    cvFileName: "",
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [cvError, setCvError] = useState<string | null>(null);
  useReveal();

  const handleChange =
    (field: keyof HiringFormData) =>
    (
      event: ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      setForm((current) => ({ ...current, [field]: event.target.value }));
    };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setCvError(null);
    if (!file) {
      setCvFile(null);
      setForm((current) => ({ ...current, cvBase64: "", cvFileName: "" }));
      return;
    }

    // Validate type
    const allowedExtensions = ["pdf", "doc", "docx"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
    if (!allowedExtensions.includes(fileExtension)) {
      const errorMsg =
        t("hiring.form.cvErrorType") ||
        "Invalid file type. Only PDF, DOC, and DOCX are allowed.";
      setCvError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    // Validate size
    if (file.size > 5 * 1024 * 1024) {
      const errorMsg =
        t("hiring.form.cvErrorSize") || "File size exceeds 5MB limit.";
      setCvError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    setCvFile(file);

    // Convert to base64
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64Data = result.split(",")[1];
      setForm((current) => ({
        ...current,
        cvBase64: base64Data,
        cvFileName: file.name,
      }));
    };
    reader.onerror = () => {
      const errorMsg = t("errors.fileReadFailed") || "Failed to read the file.";
      setCvError(errorMsg);
      toast.error(errorMsg);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = () => {
    setCvFile(null);
    setCvError(null);
    setForm((current) => ({ ...current, cvBase64: "", cvFileName: "" }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.cvBase64) {
      const errorMsg = t("errors.cvRequired") || "Please upload your CV.";
      setCvError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    setSubmitting(true);
    try {
      await hiringService.submitApplication(form);
      setSent(true);
      toast.success(
        t("hiring.form.success") || "Application submitted successfully!",
      );
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        linkedin: "",
        positionName: "",
        cvBase64: "",
        cvFileName: "",
      });
      setCvFile(null);
      setCvError(null);
    } catch {
      toast.error(
        t("errors.submitFailed") || "Something went wrong. Please try again.",
      );
    }
    setSubmitting(false);
  };

  const handleReset = () => {
    setSent(false);
  };

  return (
    <main className="page-main">
      <section className="section hiring-section">
        <div className="container hiring-grid">
          <div className="hiring-left reveal">
            <span className="hiring-tagline">{t("nav.hiring")}</span>
            <h1 className="hiring-heading">{t("hiring.title")}</h1>
            <h2 className="hiring-subheading">{t("hiring.subtitle")}</h2>
            <p className="hiring-desc">{t("hiring.desc")}</p>

            <div className="hiring-perks-list">
              <InfoCard
                title={t("hiring.perk1.title")}
                description={t("hiring.perk1.desc")}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                }
              />

              <InfoCard
                title={t("hiring.perk2.title")}
                description={t("hiring.perk2.desc")}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                }
              />
            </div>
          </div>

          <div className="hiring-right reveal reveal-delay-2">
            <div className="hiring-form-container">
              {sent ? (
                <SuccessView
                  title={t("hiring.form.successTitle")}
                  message={t("hiring.form.success")}
                  submessage={t("hiring.form.successSub")}
                  onReset={handleReset}
                />
              ) : (
                <form onSubmit={handleSubmit} className="hiring-form-modern">
                  <Input
                    id="positionName"
                    type="text"
                    label={t("hiring.form.position") || "Position Applied For"}
                    value={form.positionName}
                    onChange={handleChange("positionName")}
                    required
                    floating
                  />
                  <div className="hiring-form-row">
                    <Input
                      id="firstName"
                      label={t("contact.form.firstName")}
                      value={form.firstName}
                      onChange={handleChange("firstName")}
                      required
                      floating
                    />
                    <Input
                      id="lastName"
                      label={t("contact.form.lastName")}
                      value={form.lastName}
                      onChange={handleChange("lastName")}
                      required
                      floating
                    />
                  </div>

                  <Input
                    id="email"
                    type="email"
                    label={t("contact.form.email")}
                    value={form.email}
                    onChange={handleChange("email")}
                    required
                    floating
                  />

                  <Input
                    id="phone"
                    type="tel"
                    label={t("hiring.form.phone")}
                    value={form.phone}
                    onChange={handleChange("phone")}
                    required
                    floating
                  />

                  <Input
                    id="linkedin"
                    type="url"
                    label={t("hiring.form.linkedin")}
                    value={form.linkedin}
                    onChange={handleChange("linkedin")}
                    required
                    floating
                  />

                  <div className="hiring-file-upload-container">
                    <div
                      className={`hiring-file-dropzone ${cvError ? "dropzone-error" : ""} ${cvFile ? "dropzone-has-file" : ""}`}
                    >
                      <input
                        type="file"
                        id="cv-upload"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="hiring-file-input"
                      />
                      {!cvFile ? (
                        <label
                          htmlFor="cv-upload"
                          className="hiring-file-placeholder"
                        >
                          <svg
                            className="hiring-file-icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                          </svg>
                          <span className="hiring-file-text-main">
                            {t("hiring.form.cv")}
                          </span>
                          <span className="hiring-file-text-sub">
                            {t("hiring.form.cvHint")}
                          </span>
                        </label>
                      ) : (
                        <div className="hiring-file-selected">
                          <div className="hiring-file-info">
                            <svg
                              className="hiring-file-icon-doc"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                              <line x1="16" y1="13" x2="8" y2="13" />
                              <line x1="16" y1="17" x2="8" y2="17" />
                              <polyline points="10 9 9 9 8 9" />
                            </svg>
                            <span className="hiring-file-name">
                              {cvFile.name}
                            </span>
                            <span className="hiring-file-size">
                              ({(cvFile.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <button
                            type="button"
                            className="hiring-file-remove"
                            onClick={handleRemoveFile}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              width="18"
                              height="18"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                    {cvError && (
                      <span className="hiring-file-error-msg">{cvError}</span>
                    )}
                  </div>

                  <Button type="submit" variant="modern" loading={submitting}>
                    {t("hiring.form.submit")}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Hiring;
